import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import { getUserInfo } from '../api/auth';

interface UserInfoContextType {
  userName: string;
  avatarUrl: string;
  signature: string;
  setUserName: (name: string) => void;
  setAvatarUrl: (url: string) => void;
  setSignature: (sig: string) => void;
}

const DEFAULT_AVATAR = '/images/profile/profile-avatar-purple-001.jpg';
const DEFAULT_SIGNATURE = '天命之学，洞察人生轨迹，顺天应人，方得大道';

const STORAGE_KEYS = {
  userName: 'user-info-name',
  avatarUrl: 'user-info-avatar',
  signature: 'user-info-signature',
};

const UserInfoContext = createContext<UserInfoContextType | undefined>(undefined);

export function UserInfoProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const [userName, setUserNameState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.userName) || '头狼'
  );
  const [avatarUrl, setAvatarUrlState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.avatarUrl) || DEFAULT_AVATAR
  );
  const [signature, setSignatureState] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.signature) || DEFAULT_SIGNATURE
  );

  // 登录后从服务端拉取用户信息（昵称/头像/签名），用于面板展示；登出时还原默认
  useEffect(() => {
    if (!isLoggedIn) {
      setUserNameState(localStorage.getItem(STORAGE_KEYS.userName) || '头狼');
      setAvatarUrlState(localStorage.getItem(STORAGE_KEYS.avatarUrl) || DEFAULT_AVATAR);
      setSignatureState(localStorage.getItem(STORAGE_KEYS.signature) || DEFAULT_SIGNATURE);
      return;
    }
    let cancelled = false;
    getUserInfo()
      .then((user) => {
        if (cancelled) return;
        if (user.nickname) setUserNameState(user.nickname);
        if (user.avatar) setAvatarUrlState(user.avatar);
        if (user.signature) setSignatureState(user.signature);
      })
      .catch(() => {
        // 拉取失败（如 cookie 尚未建立）时保留本地值，不阻塞
      });
    return () => { cancelled = true; };
  }, [isLoggedIn]);

  const setUserName = useCallback((name: string) => {
    localStorage.setItem(STORAGE_KEYS.userName, name);
    setUserNameState(name);
  }, []);

  const setAvatarUrl = useCallback((url: string) => {
    localStorage.setItem(STORAGE_KEYS.avatarUrl, url);
    setAvatarUrlState(url);
  }, []);

  const setSignature = useCallback((sig: string) => {
    localStorage.setItem(STORAGE_KEYS.signature, sig);
    setSignatureState(sig);
  }, []);

  return (
    <UserInfoContext.Provider value={{ userName, avatarUrl, signature, setUserName, setAvatarUrl, setSignature }}>
      {children}
    </UserInfoContext.Provider>
  );
}

export function useUserInfo() {
  const context = useContext(UserInfoContext);
  if (!context) throw new Error('useUserInfo must be used within UserInfoProvider');
  return context;
}
